import { Instagram, Facebook, Phone, Mail, MapPin } from "lucide-react";
import { Logo, WhatsAppIcon, FadeUp } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";
import { SITE, NAV_HREFS, waLink, scrollToSection } from "../data/site";

export default function Footer() {
  const { T } = useLang();
  const socials = [
    { name: "Instagram", icon: Instagram, url: SITE.social.instagram, testId: "social-instagram" },
    { name: "Facebook", icon: Facebook, url: SITE.social.facebook, testId: "social-facebook" },
  ];

  return (
    <footer data-testid="main-footer" className="relative mt-10 bg-federasol-blush">
      {/* Thermal wave divider */}
      <svg
        data-testid="thermal-wave-svg"
        className="absolute -top-1 left-0 h-14 w-full text-federasol-cream md:h-20"
        viewBox="0 0 1440 80"
        preserveAspectRatio="none"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M0,64 C240,20 480,8 720,28 C960,48 1200,56 1440,32 L1440,80 L0,80 Z" />
        <path
          d="M0,70 C280,40 560,34 840,48 C1060,58 1260,62 1440,52"
          fill="none"
          stroke="#D98F9E"
          strokeWidth="2"
          opacity="0.5"
        />
      </svg>

      <div className="container-x pb-10 pt-16 md:pt-32">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-[1.4fr_1fr_1.3fr] md:gap-12">
          <FadeUp className="col-span-2 md:col-span-1">
            <Logo />
            <p className="mt-6 max-w-xs font-serif text-lg italic leading-relaxed text-federasol-cocoa/80">
              {T.footer.tagline}
            </p>
            <div className="mt-7 flex items-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={s.testId}
                  aria-label={s.name}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-federasol-terra/25 text-federasol-terra transition-all duration-300 hover:-translate-y-1 hover:bg-federasol-rose hover:text-white"
                >
                  <s.icon className="h-5 w-5" strokeWidth={1.7} />
                </a>
              ))}
              <a
                href={waLink(T.wa.default)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-icon"
                aria-label="WhatsApp"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-federasol-terra/25 text-federasol-terra transition-all duration-300 hover:-translate-y-1 hover:bg-[#1FAF54] hover:text-white"
              >
                <WhatsAppIcon className="h-5 w-5" />
              </a>
            </div>
          </FadeUp>

          <FadeUp delay={0.08}>
            <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-federasol-terra">
              {T.footer.navTitle}
            </h3>
            <ul className="mt-6 space-y-3">
              {NAV_HREFS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={(e) => scrollToSection(e, l.href)}
                    data-testid={`footer-nav-${l.key}`}
                    className="text-sm font-medium text-federasol-cocoa/75 transition-colors hover:text-federasol-terra"
                  >
                    {T.nav[l.key]}
                  </a>
                </li>
              ))}
            </ul>
          </FadeUp>

          <FadeUp delay={0.16}>
            <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-federasol-terra">
              {T.footer.contactTitle}
            </h3>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3 text-federasol-cocoa/80">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-federasol-terra" />
                {SITE.address}
              </li>
              <li>
                <a href={SITE.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-3 text-federasol-cocoa/80 transition-colors hover:text-federasol-terra">
                  <Phone className="h-4 w-4 shrink-0 text-federasol-terra" />
                  {SITE.phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href={waLink(T.wa.default)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="flex items-center gap-3 text-federasol-cocoa/80 transition-colors hover:text-federasol-terra"
                >
                  <WhatsAppIcon className="h-4 w-4 shrink-0 text-federasol-terra" />
                  {SITE.whatsappDisplay}
                </a>
              </li>
              <li>
                <a href={SITE.emailHref} data-testid="footer-email-link" className="flex items-center gap-3 text-federasol-cocoa/80 transition-colors hover:text-federasol-terra">
                  <Mail className="h-4 w-4 shrink-0 text-federasol-terra" />
                  {SITE.email}
                </a>
              </li>
            </ul>
          </FadeUp>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-3 border-t border-federasol-terra/15 pt-7 text-xs text-federasol-muted sm:flex-row">
          <p data-testid="footer-copyright">{T.footer.copyright}</p>
          <p>{T.footer.addressLine}</p>
        </div>
      </div>
    </footer>
  );
}
